'use client';

import { useState } from 'react';

import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@/lib/utils';

interface LinkPreviewProps {
  children: React.ReactNode;
  url: string;
  className?: string;
}

export const LinkPreview = ({ children, url, className }: LinkPreviewProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <HoverCardPrimitive.Root
      openDelay={50}
      closeDelay={100}
      onOpenChange={setIsOpen}
    >
      <HoverCardPrimitive.Trigger
        className={cn(
          'text-foreground underline underline-offset-4 decoration-dotted ' +
            // Under Arabic the dotted rule merges with the letters' own descender
            // dots — سير reads as ببير at heading size. Solid + more offset in RTL.
            'rtl:decoration-solid rtl:underline-offset-8',
          className,
        )}
      >
        {children}
      </HoverCardPrimitive.Trigger>
      <AnimatePresence>
        {isOpen && (
          <HoverCardPrimitive.Content
            className="z-50"
            side="top"
            align="center"
            sideOffset={10}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.6 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  type: 'spring',
                  stiffness: 260,
                  damping: 15,
                },
              }}
              exit={{
                opacity: 0,
                y: 20,
                scale: 0.6,
                transition: {
                  type: 'spring',
                  stiffness: 260,
                  damping: 20,
                },
              }}
              className="border-border bg-background overflow-hidden rounded-lg border shadow-lg"
            >
              <div className="bg-muted/50 px-4 py-2">
                <p className="text-muted-foreground truncate text-xs">{url}</p>
              </div>
              <div className="p-4">
                <p className="text-foreground text-sm font-medium">
                  {children}
                </p>
              </div>
            </motion.div>
          </HoverCardPrimitive.Content>
        )}
      </AnimatePresence>
    </HoverCardPrimitive.Root>
  );
};
